import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon, IconButton, KpiCard } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { EmptyState } from '@ds/feedback';
import { Input, SearchField } from '@ds/forms';
import { Pagination, SegmentedControl, Tabs } from '@ds/navigation';
import { ExportMenu } from '@/comuns/ExportMenu';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples, diaIso, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { PreferenciaSalva } from '@/data/cadastros';
import type { CadastrosFinanceiros, DetalheDoTitulo, Transferencia } from '@/data/financeiroAvulso';
import { valorEmVigor } from '@/data/preferencias';
import type { AbaDoFinanceiro, CargaDoFinanceiro, CategoriaFinanceira, LinhaDeCompetencia, LinhaDeFluxo, Movimentacao, SituacaoDoTitulo, TipoDeTitulo, Titulo } from '@/data/tipos';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import { useRotaAtual } from '@/router/useRotaAtual';
import { rotaPorId } from '@/router/rotas';
import { BaixaModal } from './financeiro/BaixaModal';
import { CategoriasDeConta } from './financeiro/CategoriasDeConta';
import { TiposDeServico } from './financeiro/TiposDeServico';
import { ContasFinanceiras } from './financeiro/ContasFinanceiras';
import { DetalheDoTituloModal } from './financeiro/DetalheDoTituloModal';
import { Gateways } from './financeiro/Gateways';
import { LancamentoModal } from './financeiro/LancamentoModal';
import { MetodosDePagamento } from './financeiro/MetodosDePagamento';
import { ComposicaoDoPeriodoModal, LancamentoDoExtratoModal, LancamentosDaCategoriaModal } from './financeiro/ModaisDeLeitura';
import type { ComposicaoDoPeriodoModalProps } from './financeiro/ModaisDeLeitura';
import { FluxoDeCaixa, RelatorioDeCompetencia, mesCurto } from './financeiro/Relatorios';
import { PainelDeNotasFiscais } from './notasFiscais/PainelDeNotasFiscais';

const POR_PAGINA = 10;

const PORTADAS: AbaDoFinanceiro[] = [
    'visao', 'receber', 'pagar', 'extrato', 'contas', 'categorias', 'metodos',
    'competencia', 'fluxo-diario', 'fluxo-mensal', 'categorias-contas', 'maquininha', 'notas',
];

/** Abas em que a busca no topo tem o que filtrar. */
const COM_BUSCA: AbaDoFinanceiro[] = ['visao', 'receber', 'pagar', 'extrato'];

/** Abas que listam página a página. */
const COM_PAGINACAO = COM_BUSCA;

/** Abas que mostram os indicadores do módulo no topo. Formas de pagamento trazem os seus. */
const COM_INDICADORES: AbaDoFinanceiro[] = ['visao', 'receber', 'pagar', 'extrato', 'contas', 'categorias', 'competencia', 'categorias-contas'];

/**
 * Abas que leem um mês: o extrato, os relatórios, o custo das formas de
 * pagamento. As listas de títulos não entram — elas mostram tudo o que está em
 * aberto, e um seletor ali pareceria filtrar o que não filtra.
 */
const COM_MES: AbaDoFinanceiro[] = ['extrato', 'contas', 'categorias', 'metodos', 'competencia', 'fluxo-diario', 'fluxo-mensal', 'maquininha'];

/** `YYYY-MM` somado de `delta` meses. */
function mesVizinho(mes: string, delta: number): string {
    const [ano, m] = mes.split('-').map(Number);
    const d = new Date(Date.UTC(ano ?? 1970, (m ?? 1) - 1 + delta, 1));
    return `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** Onde se lança título avulso. */
const COM_LANCAMENTO: AbaDoFinanceiro[] = ['visao', 'receber', 'pagar', 'extrato'];

const SITUACAO: Record<SituacaoDoTitulo, { rotulo: string; tom: 'success' | 'warning' | 'neutral' | 'danger' }> = {
    aberto: { rotulo: 'Em aberto', tom: 'warning' },
    parcial: { rotulo: 'Parcial', tom: 'warning' },
    pago: { rotulo: 'Quitado', tom: 'success' },
    cancelado: { rotulo: 'Cancelado', tom: 'neutral' },
};

function abaDaRota(pathname: string): AbaDoFinanceiro {
    const ultimo = pathname.split('/').pop() ?? '';
    return (PORTADAS as string[]).includes(ultimo) ? (ultimo as AbaDoFinanceiro) : 'visao';
}

export interface CargaDaTelaDoFinanceiro extends CargaDoFinanceiro {
    /** Transferências até o fim do mês de referência: entram no saldo das contas. */
    transferencias: Transferencia[];
    preferencias: PreferenciaSalva[];
    /** Só nas abas de contas e de formas de pagamento. */
    cadastros: CadastrosFinanceiros | null;
}

export async function carregarFinanceiro({ request }: LoaderFunctionArgs): Promise<CargaDaTelaDoFinanceiro> {
    const url = new URL(request.url);
    const aba = abaDaRota(url.pathname);
    const mes = url.searchParams.get('mes') ?? diaIso().slice(0, 7);
    const [carga, transferencias, preferencias, cadastros] = await Promise.all([
        dados().financeiro({
            aba,
            busca: url.searchParams.get('q') ?? undefined,
            mes: url.searchParams.get('mes') ?? undefined,
            pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
            porPagina: POR_PAGINA,
        }),
        dados().transferencias(mes),
        dados().preferencias(),
        aba === 'contas' || aba === 'metodos' ? dados().cadastrosFinanceiros() : Promise.resolve(null),
    ]);
    return { ...carga, transferencias, preferencias, cadastros };
}

export function Financeiro() {
    const carga = useLoaderData() as CargaDaTelaDoFinanceiro;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const handle = useRotaAtual();
    const [baixa, setBaixa] = useState<Titulo | null>(null);
    /* O regime é da leitura, não do dado: quem alterna competência e caixa está
       trocando de pergunta, não de recorte — e não vale disparar loader por isso. */
    const [regime, setRegime] = useState<'competencia' | 'caixa'>('competencia');
    const [lancamento, setLancamento] = useState<{ tipo: TipoDeTitulo } | DetalheDoTitulo | null>(null);
    /* O título aberto vive no endereço, como o orçamento em Vendas: voltar o
       reabre e o link leva direto a ele. */
    const tituloAberto = params.get('titulo');
    const setTituloAberto = (id: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (id) proximos.set('titulo', id);
            else proximos.delete('titulo');
            return proximos;
        });
    };
    const [movimento, setMovimento] = useState<Movimentacao | null>(null);
    const [composicao, setComposicao] = useState<Omit<ComposicaoDoPeriodoModalProps, 'onFechar' | 'onVerLancamentos'> & { mes: string } | null>(null);
    const [categoriaAberta, setCategoriaAberta] = useState<CategoriaFinanceira | null>(null);

    const rota = rotaPorId('financeiro');
    const subrota = handle?.subrota?.path ?? 'visao';
    const aba = carga.aba;
    const hoje = diaIso();
    const mesDeReferencia = params.get('mes') ?? hoje.slice(0, 7);
    const recarregar = () => { void revalidator.revalidate(); };
    const categoriaPadrao = valorEmVigor('fin.cat-receitas', carga.preferencias);

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (!valor) proximos.delete(chave);
            else proximos.set(chave, valor);
            if (chave !== 'pagina') proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    const editarTitulo = async (t: Titulo) => {
        try {
            setLancamento(await dados().detalheDoTitulo(t.id));
        } catch (e) {
            aviso({ title: 'Não foi possível abrir o título', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const colunasDeTitulo: DataTableColumn<Titulo>[] = [
        { key: 'descricao', header: 'Descrição', sortable: true },
        { key: 'contraparte', header: aba === 'pagar' ? 'Fornecedor' : 'Paciente' },
        { key: 'categoria', header: 'Categoria', render: (t) => t.categoria ?? '—' },
        {
            key: 'vencimento', header: 'Vencimento', sortable: true,
            render: (t) => {
                /* Vencido é conta de data: campo gravado ficaria "aberto" para
                   sempre no título que venceu ontem e ninguém tocou. */
                const vencido = t.vencimento < hoje && t.situacao !== 'pago';
                return (
                    <span style={{ color: vencido ? 'var(--danger-strong)' : 'var(--text-primary)', fontWeight: vencido ? 600 : 400 }}>
                        {dataSimples(t.vencimento)}{vencido ? ' · vencido' : ''}
                    </span>
                );
            },
        },
        { key: 'situacao', header: 'Situação', render: (t) => <Badge tone={SITUACAO[t.situacao].tom} dot>{SITUACAO[t.situacao].rotulo}</Badge> },
        {
            key: 'valorCentavos', header: 'Valor', align: 'right',
            render: (t) => (
                <span>
                    {moeda(t.valorCentavos)}
                    {t.pagoCentavos > 0 && t.situacao !== 'pago' && (
                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>falta {moeda(t.valorCentavos - t.pagoCentavos)}</span>
                    )}
                </span>
            ),
        },
        {
            key: 'acoes', header: '', align: 'right', width: 150,
            render: (t) => (
                <span data-parar-linha style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    {t.situacao !== 'cancelado' && (
                        <IconButton size="sm" icon={<Icon name="pencil" size={14} />} label={`Editar ${t.descricao}`} onClick={() => { void editarTitulo(t); }} />
                    )}
                    {t.situacao !== 'pago' && t.situacao !== 'cancelado' && (
                        <Button size="sm" variant="secondary" onClick={() => setBaixa(t)}>
                            {t.tipo === 'receber' ? 'Receber' : 'Pagar'}
                        </Button>
                    )}
                </span>
            ),
        },
    ];

    const colunasDeMovimento: DataTableColumn<Movimentacao>[] = [
        { key: 'pagoEm', header: 'Data', sortable: true, render: (m) => dataSimples(m.pagoEm) },
        { key: 'descricao', header: 'Descrição' },
        { key: 'contraparte', header: 'Contraparte' },
        { key: 'conta', header: 'Conta', render: (m) => m.conta ?? '—' },
        { key: 'metodo', header: 'Forma', render: (m) => m.metodo ?? '—' },
        { key: 'taxaCentavos', header: 'Taxa', align: 'right', render: (m) => (m.taxaCentavos ? moeda(m.taxaCentavos) : '—') },
        {
            key: 'liquidoCentavos', header: 'Líquido', align: 'right',
            render: (m) => (
                <span style={{ color: m.tipo === 'receber' ? 'var(--success-strong)' : 'var(--danger-strong)', fontWeight: 600 }}>
                    {m.tipo === 'receber' ? '+' : '−'}{moeda(m.liquidoCentavos)}
                    {m.liquidadoEm && m.liquidadoEm > hoje && (
                        <span style={{ display: 'block', fontSize: 11.5, fontWeight: 400, color: 'var(--text-tertiary)' }}>
                            entra em {dataSimples(m.liquidadoEm)}
                        </span>
                    )}
                </span>
            ),
        },
    ];

    const exportar = aba === 'competencia' ? (
        <ExportMenu<LinhaDeCompetencia>
            label="Exportar" size="md"
            payload={{
                title: 'Relatório de competência', subtitle: `${carga.competencia.length} meses`, rows: carga.competencia,
                columns: [
                    { header: 'Mês', value: (l) => mesCurto(l.mes) },
                    { header: 'Receita', value: (l) => moeda(l.receitaCentavos) },
                    { header: 'Despesa', value: (l) => moeda(l.despesaCentavos) },
                    { header: 'Resultado', value: (l) => moeda(l.receitaCentavos - l.despesaCentavos) },
                    { header: 'Entradas de caixa', value: (l) => moeda(l.caixaEntradasCentavos) },
                    { header: 'Saídas de caixa', value: (l) => moeda(l.caixaSaidasCentavos) },
                ],
            }}
        />
    ) : aba === 'fluxo-diario' || aba === 'fluxo-mensal' ? (
        <ExportMenu<LinhaDeFluxo>
            label="Exportar" size="md"
            payload={{
                title: aba === 'fluxo-diario' ? 'Fluxo de caixa diário' : 'Fluxo de caixa mensal',
                subtitle: aba === 'fluxo-diario' ? mesDeReferencia : 'últimos seis meses',
                rows: carga.fluxo,
                columns: [
                    { header: aba === 'fluxo-diario' ? 'Dia' : 'Mês', value: (l) => (aba === 'fluxo-diario' ? dataSimples(l.chave) : mesCurto(l.chave)) },
                    { header: 'Entradas', value: (l) => moeda(l.entradasCentavos) },
                    { header: 'Saídas', value: (l) => moeda(l.saidasCentavos) },
                    { header: aba === 'fluxo-diario' ? 'Saldo acumulado' : 'Resultado', value: (l) => moeda(l.saldoCentavos) },
                ],
            }}
        />
    ) : aba === 'maquininha' || aba === 'notas' ? null : aba === 'extrato' ? (
        <ExportMenu<Movimentacao>
            label="Exportar" size="md"
            carregarTudo={async () => (await dados().financeiro({
                aba, busca: params.get('q') ?? undefined, mes: params.get('mes') ?? undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS,
            })).movimentacoes}
            payload={{
                title: 'Extrato', subtitle: `${carga.total} movimentações`, rows: carga.movimentacoes,
                columns: [
                    { header: 'Data', value: (m) => dataSimples(m.pagoEm) },
                    { header: 'Descrição', value: (m) => m.descricao },
                    { header: 'Contraparte', value: (m) => m.contraparte },
                    { header: 'Conta', value: (m) => m.conta ?? '' },
                    { header: 'Forma', value: (m) => m.metodo ?? '' },
                    { header: 'Valor', value: (m) => moeda(m.valorCentavos) },
                    { header: 'Taxa', value: (m) => moeda(m.taxaCentavos) },
                    { header: 'Líquido', value: (m) => moeda(m.liquidoCentavos) },
                ],
            }}
        />
    ) : (
        <ExportMenu<Titulo>
            label="Exportar" size="md"
            carregarTudo={async () => (await dados().financeiro({
                aba, busca: params.get('q') ?? undefined, mes: params.get('mes') ?? undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS,
            })).titulos}
            payload={{
                title: 'Títulos', subtitle: `${carga.total} registros`, rows: carga.titulos,
                columns: [
                    { header: 'Descrição', value: (t) => t.descricao },
                    { header: 'Contraparte', value: (t) => t.contraparte },
                    { header: 'Vencimento', value: (t) => dataSimples(t.vencimento) },
                    { header: 'Situação', value: (t) => SITUACAO[t.situacao].rotulo },
                    { header: 'Valor', value: (t) => moeda(t.valorCentavos) },
                    { header: 'Pago', value: (t) => moeda(t.pagoCentavos) },
                ],
            }}
        />
    );

    const transferenciasDoMes = carga.transferencias.filter((t) => t.data.startsWith(mesDeReferencia));

    /* A categoria soma as filhas: "Serviços" é o total de "Serviços > Injetáveis" e das irmãs. */
    const idsDaCategoria = (id: string) => [id, ...carga.categoriasDeConta.filter((c) => c.paiId === id).map((c) => c.id)];
    const totalDaCategoria = (id: string) => {
        const ids = new Set(idsDaCategoria(id));
        return carga.porCategoria.filter((p) => ids.has(p.categoriaId)).reduce((s, p) => s + p.totalCentavos, 0);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Financeiro"
                description="Contas a receber, a pagar e o que já entrou"
                actions={(
                    <>
                        {exportar}
                        {COM_LANCAMENTO.includes(aba) && (
                            <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setLancamento({ tipo: aba === 'pagar' ? 'pagar' : 'receber' })}>
                                Novo lançamento
                            </Button>
                        )}
                    </>
                )}
            >
                <Tabs
                    ariaLabel="Seções do financeiro" activeId={subrota}
                    onChange={(id) => navigate({ pathname: `/app/financeiro/${id}`, search: params.toString() ? `?${params}` : '' })}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: c.path === 'receber' ? carga.totais.receber : c.path === 'pagar' ? carga.totais.pagar : undefined,
                    }))}
                />
            </PageHeader>

            {COM_MES.includes(aba) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {aba === 'competencia' || aba === 'fluxo-mensal' ? 'Seis meses até' : 'Mês de referência'}
                    </span>
                    <IconButton size="sm" icon={<Icon name="chevron-left" size={14} />} label="Mês anterior" onClick={() => trocarParam('mes', mesVizinho(mesDeReferencia, -1))} />
                    <Input
                        type="month" aria-label="Mês de referência" value={mesDeReferencia} style={{ width: 170 }}
                        onChange={(v) => { if (/^\d{4}-\d{2}$/.test(v)) trocarParam('mes', v === hoje.slice(0, 7) ? null : v); }}
                    />
                    <IconButton size="sm" icon={<Icon name="chevron-right" size={14} />} label="Mês seguinte" onClick={() => trocarParam('mes', mesVizinho(mesDeReferencia, 1))} />
                    {mesDeReferencia !== hoje.slice(0, 7) && (
                        <Button size="sm" variant="ghost" onClick={() => trocarParam('mes', null)}>Mês atual</Button>
                    )}
                </div>
            )}

            {/* Duas fileiras de indicador na mesma tela competem entre si: onde
                a seção traz os seus, os do módulo saem. */}
            {COM_INDICADORES.includes(aba) && (
                <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                    {carga.indicadores.map((i) => (
                        <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                    ))}
                </div>
            )}

            {COM_BUSCA.includes(aba) && (
                <FilterBar
                    search={<SearchField value={params.get('q') ?? ''} onChange={(v) => trocarParam('q', v || null)} placeholder="Buscar por descrição ou nome" width={280} />}
                    resultLabel={`${carga.total} ${aba === 'extrato' ? 'movimentações' : 'títulos'}`}
                />
            )}

            {aba === 'extrato' && (
                <>
                    <DataTable
                        columns={colunasDeMovimento} rows={carga.movimentacoes} rowKey="id"
                        onRowClick={(m) => setMovimento(m)}
                        rowLabel={(m) => `${m.descricao} · ${dataSimples(m.pagoEm)}`}
                        emptyMessage="Nenhuma movimentação no mês."
                    />
                    {transferenciasDoMes.length > 0 && (
                        <Card title="Transferências entre contas" subtitle="Não são receita nem despesa: não entram no resultado" padding={0}>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                {transferenciasDoMes.map((t, i) => (
                                    <li key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 18px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)', fontSize: 13 }}>
                                        <span style={{ width: 84, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{dataSimples(t.data)}</span>
                                        <span style={{ flex: 1, minWidth: 160 }}>{t.origem} → {t.destino}{t.observacao ? ` · ${t.observacao}` : ''}</span>
                                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{moeda(t.valorCentavos)}</strong>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    )}
                </>
            )}

            {(aba === 'visao' || aba === 'receber' || aba === 'pagar') && (
                <DataTable
                    columns={colunasDeTitulo} rows={carga.titulos} rowKey="id"
                    onRowClick={(t) => setTituloAberto(t.id)}
                    rowLabel={(t) => `${t.descricao} · ${t.contraparte}`}
                    emptyMessage="Nenhum título neste recorte."
                />
            )}

            {aba === 'contas' && carga.cadastros && (
                <ContasFinanceiras
                    contas={carga.contas}
                    cadastros={carga.cadastros}
                    transferencias={carga.transferencias}
                    movimentacoes={carga.movimentacoes}
                    conciliacaoLigada={valorEmVigor('fin.conciliacao', carga.preferencias) === true}
                    onMudou={recarregar}
                />
            )}

            {aba === 'categorias' && (
                <Card title="Categorias" subtitle={`Competência de ${mesCurto(mesDeReferencia)} · clique para ver os lançamentos`} padding={18}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {carga.categorias.map((c) => (
                            <button
                                key={c.id} type="button" onClick={() => setCategoriaAberta(c)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', cursor: 'pointer', textAlign: 'left' }}
                            >
                                <Badge tone={c.natureza === 'receita' ? 'success' : 'danger'} dot>{c.natureza === 'receita' ? 'Receita' : 'Despesa'}</Badge>
                                <span style={{ flex: 1, fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)' }}>{c.nome}</span>
                                <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: totalDaCategoria(c.id) ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                    {moeda(totalDaCategoria(c.id))}
                                </span>
                                <Icon name="chevron-right" size={14} style={{ color: 'var(--neutral-500)' }} />
                            </button>
                        ))}
                    </div>
                </Card>
            )}

            {aba === 'metodos' && carga.cadastros && (
                <MetodosDePagamento cadastros={carga.cadastros} movimentacoes={carga.movimentacoes} onMudou={recarregar} />
            )}

            {aba === 'competencia' && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <SegmentedControl
                            value={regime} onChange={(v) => setRegime(v as 'competencia' | 'caixa')}
                            ariaLabel="Regime do relatório"
                            options={[{ value: 'competencia', label: 'Competência' }, { value: 'caixa', label: 'Caixa' }]}
                        />
                        <span style={{ fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                            {regime === 'competencia'
                                ? 'Cada título no mês a que ele se refere, tenha sido pago ou não.'
                                : 'Cada pagamento no dia em que o dinheiro andou.'}
                        </span>
                    </div>
                    <RelatorioDeCompetencia
                        linhas={carga.competencia} regime={regime}
                        onSelecionar={(l) => setComposicao({
                            mes: l.mes,
                            titulo: `Composição de ${mesCurto(l.mes)}`,
                            descricao: 'Competência e caixa do mês',
                            linhas: [
                                { rotulo: 'Receita (competência)', valorCentavos: l.receitaCentavos, tom: 'entrada' },
                                { rotulo: 'Despesa (competência)', valorCentavos: l.despesaCentavos, tom: 'saida' },
                                { rotulo: 'Entradas de caixa', valorCentavos: l.caixaEntradasCentavos, tom: 'neutro' },
                                { rotulo: 'Saídas de caixa', valorCentavos: l.caixaSaidasCentavos, tom: 'neutro' },
                            ],
                            resultadoCentavos: l.receitaCentavos - l.despesaCentavos,
                        })}
                    />
                </>
            )}

            {(aba === 'fluxo-diario' || aba === 'fluxo-mensal') && (
                <FluxoDeCaixa
                    linhas={carga.fluxo} periodo={aba === 'fluxo-diario' ? 'dia' : 'mes'} mes={mesDeReferencia}
                    onSelecionar={(l) => setComposicao({
                        mes: l.chave.slice(0, 7),
                        titulo: `Composição de ${aba === 'fluxo-diario' ? dataSimples(l.chave) : mesCurto(l.chave)}`,
                        descricao: aba === 'fluxo-diario' ? 'O que entrou e saiu no dia' : 'O que entrou e saiu no mês',
                        linhas: [
                            { rotulo: 'Entradas', valorCentavos: l.entradasCentavos, tom: 'entrada' },
                            { rotulo: 'Saídas', valorCentavos: l.saidasCentavos, tom: 'saida' },
                        ],
                        resultadoCentavos: l.entradasCentavos - l.saidasCentavos,
                    })}
                />
            )}

            {aba === 'categorias-contas' && (
                <CategoriasDeConta
                    categorias={carga.categoriasDeConta}
                    busca={params.get('q') ?? ''}
                    onBusca={(v) => trocarParam('q', v || null)}
                    onMudou={() => revalidator.revalidate()}
                />
            )}
            {aba === 'categorias-contas' && <TiposDeServico />}

            {aba === 'notas' && <PainelDeNotasFiscais alvo="clinica" />}

            {aba === 'maquininha' && (
                <Gateways metodos={carga.metodos} movimentacoes={carga.movimentacoes} mes={mesDeReferencia} />
            )}

            {!PORTADAS.includes(subrota as AbaDoFinanceiro) && (
                <Card padding={0}>
                    <EmptyState
                        icon="chart-no-axes-column"
                        title={`${rota?.children.find((c) => c.path === subrota)?.label ?? subrota} ainda não foi portada`}
                        description="Esta seção do financeiro entra numa próxima leva."
                    />
                </Card>
            )}

            {COM_PAGINACAO.includes(aba) && (
                <Pagination page={carga.pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA} onChange={(p) => trocarParam('pagina', String(p))} />
            )}

            <BaixaModal
                titulo={baixa} contas={carga.contas} metodos={carga.metodos}
                onClose={() => setBaixa(null)} onRegistrado={() => revalidator.revalidate()}
            />

            {lancamento && (
                <LancamentoModal
                    alvo={lancamento} categorias={carga.categorias}
                    {...(typeof categoriaPadrao === 'string' ? { categoriaPadraoDaReceita: categoriaPadrao } : {})}
                    onFechar={() => setLancamento(null)} onSalvo={recarregar}
                />
            )}
            {tituloAberto && (
                <DetalheDoTituloModal
                    key={tituloAberto} tituloId={tituloAberto}
                    onFechar={() => setTituloAberto(null)}
                    onEditar={(d) => { setTituloAberto(null); setLancamento(d); }}
                    onBaixar={(t) => { setTituloAberto(null); setBaixa(t); }}
                    onMudou={recarregar}
                />
            )}
            {movimento && (
                <LancamentoDoExtratoModal
                    movimento={movimento} onFechar={() => setMovimento(null)}
                    onAbrirTitulo={(id) => { setMovimento(null); setTituloAberto(id); }}
                />
            )}
            {composicao && (
                <ComposicaoDoPeriodoModal
                    titulo={composicao.titulo} descricao={composicao.descricao} linhas={composicao.linhas} resultadoCentavos={composicao.resultadoCentavos}
                    onFechar={() => setComposicao(null)}
                    onVerLancamentos={() => {
                        const mes = composicao.mes;
                        setComposicao(null);
                        void navigate({ pathname: '/app/financeiro/extrato', search: `?mes=${mes}` });
                    }}
                />
            )}
            {categoriaAberta && (
                <LancamentosDaCategoriaModal
                    categoria={categoriaAberta} idsDaCategoria={idsDaCategoria(categoriaAberta.id)} mes={mesDeReferencia} onFechar={() => setCategoriaAberta(null)}
                    onAbrirTitulo={(id) => { setCategoriaAberta(null); setTituloAberto(id); }}
                />
            )}
        </div>
    );
}
